import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import BatchCalendar from '../../../components/timetable/BatchCalendar';

// â”€â”€â”€ Timeline: 10 AM â†’ 9 PM â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const START_MIN  = 10 * 60;
const END_MIN    = 21 * 60;
const RANGE_MINS = END_MIN - START_MIN; // 660 min
const ROW_H      = 56; // px per conflict lane
const LABEL_W    = 170; // px for faculty/room name column

const HOUR_MARKS = Array.from({ length: (END_MIN - START_MIN) / 60 + 1 }, (_, i) => {
  const h   = START_MIN / 60 + i;
  const h12 = ((h + 11) % 12) + 1;
  const pct = i / ((END_MIN - START_MIN) / 60) * 100;
  const label = h === 10 ? '10 AM' : h === 12 ? '12 PM' : h === 21 ? '9 PM' : String(h12);
  return { h, pct, label, isMajor: h === 10 || h === 12 || h === 21 };
});

const CELL_COLORS = [
  '#6366f1', '#0ea5e9', '#22c55e', '#f59e0b',
  '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4',
];
const CLASSROOMS       = ['C1', 'C2', 'C3', 'C4'];
const CLASSROOM_COLORS = { C1: '#6366f1', C2: '#0ea5e9', C3: '#f59e0b', C4: '#22c55e' };
// â”€â”€â”€ Pure helpers (defined outside component so they're stable) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const toMins = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(n => parseInt(n, 10) || 0);
  return h * 60 + m;
};
const to24h = (hhmm, period) => {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(n => parseInt(n, 10) || 0);
  let H = h % 12;
  if ((period || '').toUpperCase() === 'PM') H += 12;
  return String(H).padStart(2, '0') + ':' + String(m || 0).padStart(2, '0');
};
const pretty = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtMins = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

// Position a class as left%/width% within the timeline range
const timePos = (sM, eM) => ({
  left:  Math.max(0, Math.min(100, (sM - START_MIN) / RANGE_MINS * 100)) + '%',
  width: Math.max(1, Math.min(100 - Math.max(0, (sM - START_MIN) / RANGE_MINS * 100),
    (eM - sM) / RANGE_MINS * 100)) + '%',
});

// Assign vertical lanes so overlapping classes don't hide each other
const buildTimelineData = (rowSchedules) => {
  const classes = rowSchedules
    .map(s => {
      const sM = toMins(s.startTime);
      const eM = toMins(s.endTime);
      if (sM == null || eM == null || eM <= sM) return null;
      return { s, sM, eM };
    })
    .filter(Boolean)
    .sort((a, b) => a.sM - b.sM);

  const laneEnds = []; // tracks when each lane becomes free
  const positioned = classes.map(cls => {
    let lane = laneEnds.findIndex(end => end <= cls.sM);
    if (lane === -1) lane = laneEnds.length; // need a new lane
    laneEnds[lane] = cls.eM;
    return { ...cls, lane };
  });

  return { positioned, numLanes: Math.max(1, laneEnds.length) };
};

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const REASON_TYPES = [
  { value: 'students_late',     label: 'Students were late' },
  { value: 'students_absent',   label: 'Students were absent' },
  { value: 'official_activity', label: 'Official activity' },
  { value: 'other',             label: 'Other' },
];

const nowMins = () => {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
};

export default function TimetablePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite =
    ['admin', 'manager', 'team_lead'].includes(user?.roleKey || '') ||
    user?.userType === 'super_admin';

  const isFaculty = ['faculty', 'trainer', 'teacher'].includes(user?.roleKey || '');

  const [faculty,    setFaculty]    = useState([]);
  const [courses,    setCourses]    = useState([]);
  const [batches,    setBatches]    = useState([]);
  const [schedules,  setSchedules]  = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [alert,      setAlert]      = useState({ show: false, msg: '', type: 'success' });
  const [view,       setView]       = useState('faculty');
  // Top-level tab: 'timetable' (the existing slot-based view) or
  // 'calendar' (the new batch calendar with course progress). Persisted
  // to localStorage so a refresh keeps the user's last view.
  const [tab,        setTab]        = useState(() => {
    try { return localStorage.getItem('timetableTab') || 'timetable'; } catch (_) { return 'timetable'; }
  });
  const switchTab = (t) => { setTab(t); try { localStorage.setItem('timetableTab', t); } catch (_) {} };
  const [modal,      setModal]      = useState({ show: false, facultyId: '', startTime: '', endTime: '', editId: null });
  const [form,       setForm]       = useState({ batchId: '', courseId: '', classroom: '', notes: '' });
  const [mode,       setMode]       = useState('batch');
  const [saving,     setSaving]     = useState(false);

  // â”€â”€ Check-in state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [checkIns,      setCheckIns]      = useState([]);   // today's check-in records
  const [checkInSaving, setCheckInSaving] = useState({});   // { scheduleId: true }
  const [reasonDialog,  setReasonDialog]  = useState({ show: false, schedule: null });
  const [reasonForm,    setReasonForm]    = useState({ reasonType: '', reasonNote: '' });
  const [tick,          setTick]          = useState(0); // forces re-render for time-window checks

  const loadCheckIns = useCallback(async () => {
    if (!isFaculty && !canWrite) return;
    try {
      const r = await api.get('/training/work-attendance/class-checkins/today');
      setCheckIns(r.data?.data || []);
    } catch { /* silent */ }
  }, [isFaculty, canWrite]); // eslint-disable-line

  useEffect(() => { loadCheckIns(); }, [loadCheckIns]);

  // Refresh check-ins and re-evaluate active windows every minute
  useEffect(() => {
    const id = setInterval(() => {
      setTick(t => t + 1);
      loadCheckIns();
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [loadCheckIns]);

  const getCheckIn = (scheduleId) =>
    checkIns.find(c => String(c.scheduleId?._id || c.scheduleId) === String(scheduleId));

  // Class is in the "going to class" window: -5 min â†’ +5 min around start
  const isCheckInWindow = (startTime) => {
    const sm  = toMins(startTime);
    const now = nowMins();
    return sm >= 0 && now >= sm - 5 && now <= sm + 5;
  };

  // Class started 15 - 90 min ago and faculty still hasn't responded
  const isReasonWindow = (startTime) => {
    const sm  = toMins(startTime);
    const now = nowMins();
    return sm >= 0 && now >= sm + 15 && now <= sm + 90;
  };

  const handleCheckIn = async (scheduleId) => {
    setCheckInSaving(p => ({ ...p, [scheduleId]: true }));
    try {
      await api.post('/training/work-attendance/class-checkins', { scheduleId });
      await loadCheckIns();
      showAlert('You are marked as going to class!', 'success');
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to check in.', 'danger'); }
    setCheckInSaving(p => ({ ...p, [scheduleId]: false }));
  };

  const handleReasonSubmit = async () => {
    if (!reasonForm.reasonType) { showAlert('Please select a reason.', 'danger'); return; }
    const scheduleId = reasonDialog.schedule?._id;
    setCheckInSaving(p => ({ ...p, [scheduleId]: true }));
    try {
      await api.post('/training/work-attendance/class-checkins/reason', {
        scheduleId,
        reasonType: reasonForm.reasonType,
        reasonNote: reasonForm.reasonNote || undefined,
      });
      await loadCheckIns();
      setReasonDialog({ show: false, schedule: null });
      showAlert('Reason submitted.', 'success');
    } catch (e) { showAlert(e.response?.data?.message || 'Failed to submit reason.', 'danger'); }
    setCheckInSaving(p => ({ ...p, [scheduleId]: false }));
  };

  // My classes today: schedules where I am the faculty
  const mySchedules = useMemo(() =>
    schedules.filter(s => String(s.faculty?._id || s.faculty) === String(user?._id || user?.id)),
    [schedules, user]
  );

  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });

  useEffect(() => {
    api.get('/training/work-attendance/faculty')
      .then(r => setFaculty(r.data?.data || r.data?.records || r.data || []))
      .catch(() =>
        api.get('/users', { params: { roleKey: 'faculty,trainer,teacher', limit: 500 } })
          .then(r => setFaculty(r.data?.records || r.data?.data || []))
          .catch(() => {})
      );
    api.get('/training/courses', { params: { limit: 500 } })
      .then(r => setCourses(r.data?.data || r.data?.records || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/training/batches', { params: { limit: 500, status: 'ongoing,started,upcoming' } })
      .then(r => setBatches(r.data?.records || r.data?.data || []))
      .catch(() => {});
  }, []);

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/work-attendance/class-schedules', { params: { limit: 500 } });
      setSchedules(Array.isArray(r.data?.records || r.data?.data) ? (r.data?.records || r.data?.data) : []);
    } catch (e) {
      setSchedules([]);
      showAlert(e.response?.data?.message || 'Could not load schedules.', 'danger');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const batchColorMap = useMemo(() => {
    const m = {};
    batches.forEach((b, i) => { m[String(b._id)] = CELL_COLORS[i % CELL_COLORS.length]; });
    return m;
  }, [batches]);

  // Course-color map — the FIXED colour for each course, used as the
  // primary tint on every class card. This replaces the old per-batch
  // random colour so the same course always shows the same colour
  // across the timetable, batch calendar, and staff view.
  //
  // We pull courseColor from the `course` object populated on every
  // schedule. If a course doesn't have a courseColor (legacy data),
  // we fall back to a stable hash-based colour from a small palette
  // so it still renders consistently.
  const courseColorMap = useMemo(() => {
    const m = {};
    const fallback = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
    const stableColor = (key) => {
      if (!key) return fallback[0];
      let h = 0; for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
      return fallback[Math.abs(h) % fallback.length];
    };
    for (const s of schedules) {
      const cId = String(s.course?._id || s.course);
      if (!cId) continue;
      if (m[cId]) continue;
      const fromCourse = s.course?.courseColor;
      m[cId] = (typeof fromCourse === 'string' && /^#[0-9a-fA-F]{3,6}$/.test(fromCourse))
        ? fromCourse
        : stableColor(cId);
    }
    return m;
  }, [schedules]);

  // â”€â”€ Handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const batchTimes = (batch) => {
    if (!batch) return { start: '', end: '' };
    return {
      start: to24h(batch.timing?.startTime, batch.timing?.startPeriod),
      end:   to24h(batch.timing?.endTime,   batch.timing?.endPeriod),
    };
  };

  const isRoomBusy = (room) => {
    const sM = toMins(modal.startTime), eM = toMins(modal.endTime);
    if (!sM || !eM) return false;
    return schedules.some(s =>
      s.classroom === room &&
      (!modal.editId || String(s._id) !== String(modal.editId)) &&
      toMins(s.startTime) < eM && toMins(s.endTime) > sM
    );
  };

  const openFromTimeline = (e, facultyId, classroom) => {
    if (!canWrite || e.target.closest('button')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(0.99, (e.clientX - rect.left) / rect.width));
    const mins = START_MIN + Math.round(pct * RANGE_MINS / 30) * 30;
    setModal({ show: true, facultyId: facultyId || '', startTime: fmtMins(mins), endTime: fmtMins(Math.min(mins + 60, END_MIN)), editId: null });
    setForm({ batchId: '', courseId: '', classroom: classroom || '', notes: '' });
    setMode('batch');
  };

  const openEdit = (c) => {
    if (!canWrite) return;
    const batchId = c.batch?._id || c.batch || '';
    setModal({ show: true, facultyId: c.faculty?._id || c.faculty || '', startTime: c.startTime || '', endTime: c.endTime || '', editId: c._id });
    setForm({ batchId, courseId: c.course?._id || c.course || '', classroom: c.classroom || '', notes: c.notes || '' });
    setMode(batchId ? 'batch' : 'manual');
  };

  const handleBatchPick = (batchId) => {
    const batch = batches.find(b => String(b._id) === String(batchId));
    if (!batch) { setForm(f => ({ ...f, batchId: '', courseId: '' })); return; }
    const { start, end } = batchTimes(batch);
    const courseRaw = batch.course && typeof batch.course === 'object' ? batch.course._id : batch.course;
    setForm(f => ({ ...f, batchId, courseId: courseRaw ? String(courseRaw) : '' }));
    setModal(m => ({ ...m, startTime: start || m.startTime, endTime: end || m.endTime }));
  };

  const handleSave = async () => {
    if (!modal.facultyId) { showAlert('Please select a faculty.', 'danger'); return; }
    if (!form.courseId)   { showAlert('Please select a batch or course.', 'danger'); return; }
    if (!form.classroom)  { showAlert('Please select a classroom.', 'danger'); return; }
    if (!modal.startTime || !modal.endTime) { showAlert('Start and end time are required.', 'danger'); return; }
    if (toMins(modal.endTime) <= toMins(modal.startTime)) { showAlert('End time must be after start time.', 'danger'); return; }
    setSaving(true);
    try {
      const payload = { courseId: form.courseId, batchId: form.batchId || undefined, facultyId: modal.facultyId, startTime: modal.startTime, endTime: modal.endTime, classroom: form.classroom, notes: form.notes };
      if (modal.editId) {
        await api.put('/training/work-attendance/class-schedules/' + modal.editId, payload);
        showAlert('Class updated.');
      } else {
        await api.post('/training/work-attendance/class-schedules', payload);
        showAlert('Class scheduled.');
      }
      setModal({ show: false });
      loadSchedules();
    } catch (e) {
      showAlert(e.response?.data?.message || 'Failed to save.', 'danger');
    }
    setSaving(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Remove this class?')) return;
    try { await api.delete('/training/work-attendance/class-schedules/' + id); loadSchedules(); }
    catch (_) { showAlert('Could not remove.', 'danger'); }
  };

  const handleClearFaculty = async (facultyId, name) => {
    if (!canWrite || !window.confirm(`Remove ALL classes for ${name}?`)) return;
    try {
      const r = await api.delete('/training/work-attendance/class-schedules/by-faculty/' + facultyId);
      showAlert(`Removed ${r.data?.data?.deleted || 0} class(es).`);
      loadSchedules();
    } catch (e) { showAlert(e.response?.data?.message || 'Could not clear.', 'danger'); }
  };

  // â”€â”€ Shared timeline row renderer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const renderTimelineRow = ({ key, label, sublabel, onClear, rowSchedules, accentColor, onRowClick, showFaculty }) => {
    const { positioned, numLanes } = buildTimelineData(rowSchedules);
    const timelineH = numLanes * ROW_H;  // exact height for the positioned container
    const rowH      = timelineH + 10;    // row adds 5px top + 5px bottom padding

    return (
      <div key={key} style={{ display: 'flex', borderBottom: '1px solid #dee2e6', height: rowH, alignItems: 'stretch' }}>

        {/* Label column */}
        <div style={{ width: LABEL_W, minWidth: LABEL_W, padding: '6px 10px', borderRight: '2px solid #dee2e6', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2, boxSizing: 'border-box' }}>
          <div className="d-flex justify-content-between align-items-center gap-1">
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
              {sublabel && <div style={{ fontSize: 10, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sublabel}</div>}
            </div>
            {canWrite && onClear && (
              <button className="btn btn-sm btn-outline-danger flex-shrink-0"
                style={{ fontSize: 10, padding: '2px 5px', lineHeight: 1 }}
                title="Clear all today" onClick={onClear}>
                <i className="bi bi-trash" />
              </button>
            )}
          </div>
          {numLanes > 1 && (
            <div style={{ fontSize: 9, color: '#b45309', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
              <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: 9 }} />
              {numLanes} overlapping classes
            </div>
          )}
        </div>

        {/* Timeline container */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 6px' }}>
          <div style={{ position: 'relative', width: '100%', height: timelineH, background: accentColor + '0d', borderRadius: 6, cursor: canWrite ? 'pointer' : 'default' }}
            onClick={onRowClick}>

            {/* Hour gridlines */}
            {HOUR_MARKS.slice(1).map(mark => (
              <div key={mark.h} style={{ position: 'absolute', left: mark.pct + '%', top: 0, bottom: 0, borderLeft: '1px solid #e2e8f0', pointerEvents: 'none' }} />
            ))}

            {/* Class cards */}
            {positioned.map(({ s: c, sM, eM, lane }) => {
              const pos    = timePos(sM, eM);
              // Use the COURSE colour (per Issue 2 — same course
              // = same colour everywhere). Falls back to the row's
              // accent only when the course is unknown.
              const cKey   = String(c.course?._id || c.course);
              const color  = courseColorMap[cKey] || batchColorMap[String(c.batch?._id || c.batch)] || accentColor;
              const cName  = c.course?.courseName || c.course?.name || 'Class';
              const bNum   = c.batch?.batchNumber || '';
              const fName  = showFaculty
                ? [c.faculty?.firstName, c.faculty?.lastName].filter(Boolean).join(' ')
                : '';
              const cardH  = ROW_H - 8;
              const cardTop = lane * ROW_H + 4;

              return (
                <div key={c._id}
                  style={{ position: 'absolute', left: pos.left, width: pos.width, top: cardTop, height: cardH, padding: '0 2px', zIndex: lane + 1 }}
                  onClick={e => e.stopPropagation()}>
                  <div style={{ position: 'relative', background: color, color: '#fff', borderRadius: 6, height: '100%', padding: '3px 7px', fontSize: 11, lineHeight: 1.3, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>
                    {/* Edit / Remove  -  pinned to top-right, always visible */}
                    {canWrite && (
                      <div style={{ position: 'absolute', top: 3, right: 4, display: 'flex', gap: 4, zIndex: 2 }}
                        onClick={e => e.stopPropagation()}>
                        <button className="btn p-0 text-white"
                          style={{ fontSize: 10, lineHeight: 1, opacity: 0.9, background: 'rgba(0,0,0,0.2)', borderRadius: 3, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Edit" onClick={e => { e.stopPropagation(); openEdit(c); }}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button className="btn p-0 text-white"
                          style={{ fontSize: 10, lineHeight: 1, opacity: 0.9, background: 'rgba(0,0,0,0.2)', borderRadius: 3, width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          title="Remove" onClick={e => handleDelete(c._id, e)}>
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    )}
                    <div className="fw-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: canWrite ? 44 : 0 }}>
                      {cName}{bNum ? ` · ${bNum}` : ''}
                    </div>
                    {fName && (
                      <div style={{ fontSize: 10, opacity: 0.88, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <i className="bi bi-person-fill" style={{ fontSize: 8 }} /> {fName}
                      </div>
                    )}
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{pretty(c.startTime)}  -  {pretty(c.endTime)}</div>
                  </div>
                </div>
              );
            })}

            {/* Empty hint */}
            {canWrite && positioned.length === 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', paddingLeft: 10, pointerEvents: 'none' }}>
                <span style={{ fontSize: 11, color: '#cbd5e1' }}><i className="bi bi-plus" /> click to schedule</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <div>
      <AlertToast message={alert.msg} type={alert.type} show={alert.show}
        onClose={() => setAlert(p => ({ ...p, show: false }))} />

      <PageHeader
        title="Staff Timetable"
        icon="bi-calendar-week"
        subtitle={canWrite
          ? 'Static weekly schedule  -  click any lane to add a class. Changes are permanent.'
          : 'Read-only view of the staff schedule.'}
      />

      {/* Top-level tab: Timetable (slot-based grid) vs. Batch Calendar
          (faculty picker + per-batch progress). State persisted to
          localStorage so a refresh keeps the user's last view. */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="btn-group btn-group-sm" role="group" aria-label="Timetable view">
          <button type="button"
            className={`btn ${tab === 'timetable' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => switchTab('timetable')}>
            <i className="bi bi-grid-3x3-gap me-1" />
            Timetable
          </button>
          <button type="button"
            className={`btn ${tab === 'calendar' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => switchTab('calendar')}>
            <i className="bi bi-calendar3 me-1" />
            Batch Calendar
          </button>
        </div>
        {tab === 'calendar' && (
          <span className="text-muted small">
            <i className="bi bi-info-circle me-1" />
            Same course, same colour across every surface.
          </span>
        )}
      </div>

      {/* ── Batch Calendar tab — reuses existing schedule allocation
          and progress, but lays everything out as per-batch cards
          grouped by course colour. ────────────────────────────────── */}
      {tab === 'calendar' && (
        <BatchCalendar navigate={navigate} onSwitchTab={switchTab} />
      )}

      {/* ── Timetable tab — the existing slot-based grid + check-in
          panel + scheduling modal. Hidden when calendar is active so
          only one view is on screen at a time. ──────────────────── */}
      {tab === 'timetable' && (
        <>
      {/* â”€â”€ Faculty: My Classes Today (check-in panel) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {isFaculty && mySchedules.length > 0 && (
        <div className="card shadow-sm mb-3 border-0" style={{ background: '#f0fdf4' }}>
          <div className="card-header fw-semibold" style={{ background: '#dcfce7', borderBottom: '1px solid #bbf7d0', fontSize: 14 }}>
            <i className="bi bi-calendar-check me-2 text-success" />
            My Classes Today
          </div>
          <div className="card-body p-0">
            {mySchedules.map(cls => {
              const checkIn   = getCheckIn(cls._id);
              const inWindow  = isCheckInWindow(cls.startTime);
              const inReason  = isReasonWindow(cls.startTime);
              const isSaving  = checkInSaving[String(cls._id)];
              const courseName = cls.course?.courseName || cls.course?.name || 'Class';
              const batchNum   = cls.batch?.batchNumber || '';

              return (
                <div key={cls._id} className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{ borderBottom: '1px solid #dcfce7' }}>
                  <div>
                    <span className="fw-semibold" style={{ fontSize: 14 }}>{courseName}</span>
                    {batchNum && <span className="text-muted ms-2" style={{ fontSize: 12 }}>· {batchNum}</span>}
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {pretty(cls.startTime)}  -  {pretty(cls.endTime)}
                      {cls.classroom && <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>{cls.classroom}</span>}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {checkIn?.status === 'checked_in' && (
                      <span className="badge bg-success"><i className="bi bi-check-lg me-1" />Going to Class</span>
                    )}
                    {checkIn?.status === 'reason_submitted' && (
                      <span className="badge bg-warning text-dark">
                        <i className="bi bi-info-circle me-1" />
                        {REASON_TYPES.find(r => r.value === checkIn.reasonType)?.label || 'Reason submitted'}
                      </span>
                    )}
                    {!checkIn && inWindow && (
                      <button className="btn btn-success btn-sm" disabled={isSaving}
                        onClick={() => handleCheckIn(cls._id)}>
                        {isSaving
                          ? <span className="spinner-border spinner-border-sm" />
                          : <><i className="bi bi-check2-circle me-1" />Going to Class</>}
                      </button>
                    )}
                    {!checkIn && inReason && (
                      <button className="btn btn-warning btn-sm" disabled={isSaving}
                        onClick={() => { setReasonDialog({ show: true, schedule: cls }); setReasonForm({ reasonType: '', reasonNote: '' }); }}>
                        <i className="bi bi-exclamation-triangle me-1" />Submit Reason
                      </button>
                    )}
                    {!checkIn && !inWindow && !inReason && (
                      <span className="text-muted" style={{ fontSize: 12 }}>Upcoming</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        {loading && <span className="spinner-border spinner-border-sm text-primary" />}
        <div className="btn-group btn-group-sm ms-auto">
          <button className={`btn ${view === 'faculty'   ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setView('faculty')}>
            <i className="bi bi-people me-1" />Faculty
          </button>
          <button className={`btn ${view === 'classroom' ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => setView('classroom')}>
            <i className="bi bi-door-open me-1" />Classroom
          </button>
        </div>
        {canWrite && (
          <button className="btn btn-primary btn-sm"
            onClick={() => {
              setModal({ show: true, facultyId: '', startTime: '10:00', endTime: '11:00', editId: null });
              setForm({ batchId: '', courseId: '', classroom: '', notes: '' });
              setMode('batch');
            }}>
            <i className="bi bi-plus-lg me-1" />Schedule a Class
          </button>
        )}
        </div>

      {faculty.length === 0 ? (
        <div className="alert alert-warning">
          <i className="bi bi-info-circle me-1" />
          No teaching staff found.{canWrite && <> Assign the <code>Faculty</code> role to at least one user.</>}
        </div>
      ) : (
        <>
        {/* Course color legend — shared by timetable + batch calendar +
            staff view so the same course always shows the same colour
            (Issue 2). Admin can edit a course's colour from the Courses
            page; we just show a key here. */}
        {Object.keys(courseColorMap).length > 0 && (
          <div className="card shadow-sm mb-2" style={{ borderRadius: 10 }}>
            <div className="card-body py-2 px-3 d-flex align-items-center flex-wrap gap-2" style={{ fontSize: 12 }}>
              <span className="text-muted small me-1">
                <i className="bi bi-palette me-1" />Courses
              </span>
              {Object.keys(courseColorMap).map(cId => {
                const c = courses.find(x => String(x._id) === cId) || schedules.find(s => String(s.course?._id || s.course) === cId)?.course;
                const label = c?.courseName || cId;
                const code  = c?.courseCode;
                return (
                  <span key={cId} className="d-inline-flex align-items-center gap-1 px-2 py-1"
                    style={{ background: courseColorMap[cId] + '22', border: `1px solid ${courseColorMap[cId]}55`, borderRadius: 999, color: '#0f172a' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: courseColorMap[cId] }} />
                    <span style={{ fontWeight: 600 }}>{label}</span>
                    {code && <code style={{ fontSize: 9, opacity: 0.7 }}>{code}</code>}
                  </span>
                );
              })}
              {canWrite && (
                <a href="/courses" className="ms-auto small text-decoration-none">
                  <i className="bi bi-pencil-square me-1" />Edit colours
                </a>
              )}
            </div>
          </div>
        )}

        <div className="card shadow-sm" style={{ overflow: 'hidden' }}>

          {/* Time header */}
          <div style={{ display: 'flex', background: '#212529', color: '#fff', borderBottom: '2px solid #495057' }}>
            <div style={{ width: LABEL_W, minWidth: LABEL_W, padding: '6px 10px', borderRight: '1px solid #495057', fontSize: 11, fontWeight: 700 }}>
              {view === 'faculty' ? 'Faculty' : 'Classroom'}
            </div>
            <div style={{ flex: 1, position: 'relative', height: 28, padding: '0 6px' }}>
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                {HOUR_MARKS.map(mark => (
                  <div key={mark.h}
                    style={{ position: 'absolute', left: mark.pct + '%', top: 0, height: '100%', paddingLeft: 4, fontSize: 10, display: 'flex', alignItems: 'center', fontWeight: mark.isMajor ? 700 : 400, opacity: mark.isMajor ? 1 : 0.65, borderLeft: mark.pct > 0 ? '1px solid #495057' : 'none', whiteSpace: 'nowrap' }}>
                    {mark.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Rows */}
          {view === 'faculty'
            ? faculty.map((f, fi) =>
                renderTimelineRow({
                  key:         f._id,
                  label:       `${f.firstName} ${f.lastName}`,
                  sublabel:    f.email,
                  onClear:     () => handleClearFaculty(f._id, `${f.firstName} ${f.lastName}`),
                  rowSchedules: schedules.filter(s => (s.faculty?._id || s.faculty)?.toString() === f._id?.toString()),
                  accentColor: CELL_COLORS[fi % CELL_COLORS.length],
                  onRowClick:  e => openFromTimeline(e, f._id, ''),
                  showFaculty: false,
                })
              )
            : CLASSROOMS.map(room =>
                renderTimelineRow({
                  key:         room,
                  label:       room,
                  sublabel:    'Classroom',
                  onClear:     null,
                  rowSchedules: schedules.filter(s => s.classroom === room),
                  accentColor: CLASSROOM_COLORS[room] || CELL_COLORS[0],
                  onRowClick:  e => openFromTimeline(e, '', room),
                  showFaculty: true,
                })
              )
          }
        </div>
      )}
        </>
      )}

      {/* â”€â”€ Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {modal.show && (
        <div className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1050 }}
          onClick={e => { if (e.target === e.currentTarget) setModal({ show: false }); }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${modal.editId ? 'bi-pencil-square' : 'bi-plus-circle'} me-2 text-primary`} />
                  {modal.editId ? 'Edit Scheduled Class' : 'Schedule a Class'}
                </h5>
                <button className="btn-close" onClick={() => setModal({ show: false })} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Faculty <span className="text-danger">*</span></label>
<select className="form-select form-select-sm" value={modal.facultyId}
                    onChange={e => setModal(m => ({ ...m, facultyId: e.target.value }))}>
                    <option value="">— Select faculty —</option>
                    {faculty.map(f => {
                      // Show the role so the admin can tell at a glance
                      // which staff are primary faculty vs admins who
                      // also teach (multi-role). `additionalRoleKeys` is
                      // an array — render its entries comma-separated.
                      const roles = [f.roleKey, ...(f.additionalRoleKeys || [])]
                        .filter(Boolean)
                        .filter((v, i, a) => a.indexOf(v) === i);  // de-dup
                      const roleLabel = roles.length ? roles.join(', ') : '';
                      return (
                        <option key={f._id} value={f._id}>
                          {f.firstName} {f.lastName}
                          {f.email ? ` (${f.email})` : ''}
                          {roleLabel ? ` — ${roleLabel}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Source</label>
                  <div className="btn-group w-100">
                    <button type="button" className={`btn btn-sm ${mode === 'batch' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setMode('batch')}>
                      <i className="bi bi-collection me-1" />From Batch
                    </button>
                    <button type="button" className={`btn btn-sm ${mode === 'manual' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => { setMode('manual'); setForm(f => ({ ...f, batchId: '' })); }}>
                      <i className="bi bi-pencil-square me-1" />Manual Entry
                    </button>
                  </div>
                  <small className="text-muted d-block mt-1">
                    {mode === 'batch' ? 'Pick a batch  -  course and timing auto-fill.' : 'Choose course and set times manually.'}
                  </small>
                </div>
                {mode === 'batch' && (
                  <div className="mb-3">
                    <label className="form-label">Batch <small className="text-muted">(auto-fills time + course)</small></label>
                    <select className="form-select form-select-sm" value={form.batchId}
                      onChange={e => handleBatchPick(e.target.value)}>
                      <option value=""> -  Select batch  - </option>
                      {batches.map(b => {
                        const t = batchTimes(b);
                        const name = b.course?.courseName || b.course?.name || '';
                        const time = t.start && t.end ? `  -  ${pretty(t.start)} to ${pretty(t.end)}` : '';
                        return <option key={b._id} value={b._id}>{b.batchNumber} · {name}{time}</option>;
                      })}
                    </select>
                  </div>
                )}
                <div className="row g-2 mb-3">
                  <div className="col">
                    <label className="form-label">Start Time <span className="text-danger">*</span></label>
                    <input type="time" className="form-control form-control-sm" value={modal.startTime}
                      onChange={e => setModal(m => ({ ...m, startTime: e.target.value }))} />
                  </div>
                  <div className="col">
                    <label className="form-label">End Time <span className="text-danger">*</span></label>
                    <input type="time" className="form-control form-control-sm" value={modal.endTime}
                      onChange={e => setModal(m => ({ ...m, endTime: e.target.value }))} />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Course <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm" value={form.courseId}
                    onChange={e => setForm(f => ({ ...f, courseId: e.target.value }))}
                    disabled={mode === 'batch' && !!form.batchId}>
                    <option value=""> -  Select course  - </option>
                    {courses.map(c => (
                      <option key={c._id} value={c._id}>{c.courseName || c.name}{c.courseCode ? ` (${c.courseCode})` : ''}</option>
                    ))}
                  </select>
                  {mode === 'batch' && form.batchId && <small className="text-muted">Course set from batch.</small>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Classroom <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm" value={form.classroom}
                    onChange={e => setForm(f => ({ ...f, classroom: e.target.value }))}>
                    <option value="">-- Select classroom --</option>
                    {CLASSROOMS.map(r => {
                      const busy = isRoomBusy(r);
                      return <option key={r} value={r}>{r} - {busy ? 'Booked' : 'Available'}</option>;
                    })}
                  </select>
                  {form.classroom && isRoomBusy(form.classroom) && (
                    <small className="text-warning d-block mt-1">
                      <i className="bi bi-exclamation-triangle me-1" />{form.classroom} is already booked during this time.
                    </small>
                  )}
                </div>
                <div className="mb-2">
                  <label className="form-label">Notes <small className="text-muted">(optional)</small></label>
                  <input className="form-control form-control-sm" placeholder="Topic or notes..."
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setModal({ show: false })}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : <><i className="bi bi-check-lg me-1" />{modal.editId ? 'Update' : 'Schedule'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* â”€â”€ Reason Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {reasonDialog.show && reasonDialog.schedule && (
        <div className="modal fade show d-block"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1055 }}
          onClick={e => { if (e.target === e.currentTarget) setReasonDialog({ show: false, schedule: null }); }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">
                  <i className="bi bi-exclamation-triangle text-warning me-2" />
                  Why didn't you take the class?
                </h6>
                <button className="btn-close btn-sm" onClick={() => setReasonDialog({ show: false, schedule: null })} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  {reasonDialog.schedule.course?.courseName || 'Class'} at {pretty(reasonDialog.schedule.startTime)}
                  {reasonDialog.schedule.classroom && ` · ${reasonDialog.schedule.classroom}`}
                </p>
                <div className="mb-3">
                  <label className="form-label form-label-sm fw-semibold">Reason <span className="text-danger">*</span></label>
                  <div className="d-flex flex-column gap-2">
                    {REASON_TYPES.map(r => (
                      <label key={r.value} className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                        <input type="radio" name="reasonType" value={r.value}
                          checked={reasonForm.reasonType === r.value}
                          onChange={() => setReasonForm(f => ({ ...f, reasonType: r.value }))} />
                        <span style={{ fontSize: 13 }}>{r.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="form-label form-label-sm">Additional notes <small className="text-muted">(optional)</small></label>
                  <textarea className="form-control form-control-sm" rows={2}
                    placeholder="Any details..."
                    value={reasonForm.reasonNote}
                    onChange={e => setReasonForm(f => ({ ...f, reasonNote: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary btn-sm"
                  onClick={() => setReasonDialog({ show: false, schedule: null })}>
                  Cancel
                </button>
                <button className="btn btn-warning btn-sm"
                  disabled={checkInSaving[String(reasonDialog.schedule._id)]}
                  onClick={handleReasonSubmit}>
                  {checkInSaving[String(reasonDialog.schedule._id)]
                    ? <span className="spinner-border spinner-border-sm" />
                    : <><i className="bi bi-send me-1" />Submit</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
